<template>
    <div class="artigos-image-picker">
        <div
            class="picker-frame"
            :class="{
                'picker-frame--empty': !value && !uploading,
                'picker-frame--has-image': !!value,
                'picker-frame--dragover': dragover,
                'picker-frame--disabled': disabled,
            }"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="dragover = false"
            @drop.prevent="onDrop"
        >
            <img v-if="value" :src="value" :alt="alt" class="picker-image" />

            <div v-if="uploading" class="picker-overlay picker-overlay--loading">
                <v-progress-circular indeterminate color="primary" size="40" />
                <span class="mt-3 text-body-2">Enviando imagem...</span>
            </div>

            <template v-else-if="!value">
                <div class="picker-empty" @click="pickFile">
                    <div class="picker-empty-icon">
                        <v-icon size="40" color="primary">mdi-image-plus</v-icon>
                    </div>
                    <div class="picker-empty-title">Imagem principal do artigo</div>
                    <div class="picker-empty-hint">Arraste e solte aqui, ou escolha uma origem abaixo</div>
                </div>
            </template>

            <div v-else-if="!uploading" class="picker-overlay picker-overlay--actions">
                <v-btn small depressed color="white" class="mr-2" @click.stop="pickFile">
                    <v-icon left small>mdi-upload</v-icon>
                    Trocar
                </v-btn>
                <v-btn small depressed color="white" @click.stop="remove" :disabled="disabled">
                    <v-icon left small>mdi-close</v-icon>
                    Remover
                </v-btn>
            </div>
        </div>

        <div class="picker-actions">
            <v-btn
                outlined
                color="primary"
                :disabled="disabled || uploading"
                @click="pickFile"
            >
                <v-icon left small>mdi-upload</v-icon>
                Enviar do computador
            </v-btn>
            <v-btn
                outlined
                color="primary"
                :disabled="disabled || uploading"
                @click="togglePixibay"
            >
                <v-icon left small>mdi-image-search-outline</v-icon>
                {{ pixibayOpen ? 'Fechar banco de imagens' : 'Banco de imagens' }}
            </v-btn>
        </div>

        <transition name="pixibay-fade">
            <div v-if="pixibayOpen" class="pixibay-panel">
                <div class="pixibay-search">
                    <v-text-field
                        v-model="query"
                        outlined
                        dense
                        hide-details
                        prepend-inner-icon="mdi-magnify"
                        placeholder="Buscar por palavra-chave (ex.: saúde, escritório, natureza)"
                        :loading="searching"
                        clearable
                        @click:clear="resetSearch"
                    />
                </div>

                <div v-if="searching && items.length === 0" class="pixibay-skeleton-grid">
                    <div v-for="n in 8" :key="n" class="pixibay-skeleton" />
                </div>

                <div v-else-if="items.length > 0" class="pixibay-grid">
                    <button
                        v-for="(item, i) in items"
                        :key="i"
                        type="button"
                        class="pixibay-tile"
                        :class="{ 'pixibay-tile--selected': value === item.largeImageURL }"
                        @click="select(item)"
                    >
                        <img :src="item.previewURL || item.webformatURL" :alt="item.tags || 'imagem'" />
                        <div class="pixibay-tile-check">
                            <v-icon size="18" color="white">mdi-check</v-icon>
                        </div>
                    </button>
                </div>

                <div v-else-if="!searching && query" class="pixibay-empty">
                    Nenhuma imagem encontrada para "{{ query }}".
                </div>

                <div v-else class="pixibay-empty">
                    Digite uma palavra-chave para buscar imagens.
                </div>

                <div v-if="hasMore" class="pixibay-load-more">
                    <v-btn text color="primary" :loading="loadingMore" @click="loadMore">
                        Carregar mais
                    </v-btn>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import debounce from 'lodash/debounce'
import Api from '@/lib/Api'
import { emitToastr } from '@/lib/Utils'

export default {
    name: 'ArtigosImagePicker',
    props: {
        value: { type: String, default: null },
        disabled: { type: Boolean, default: false },
        alt: { type: String, default: 'Imagem principal' },
    },
    data() {
        return {
            uploading: false,
            dragover: false,
            pixibayOpen: false,
            query: '',
            page: 1,
            items: [],
            searching: false,
            loadingMore: false,
            hasMore: false,
        }
    },
    methods: {
        emitImage(url) {
            this.$emit('input', url)
        },
        pickFile() {
            if (this.disabled || this.uploading) return
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/jpeg,image/png,image/webp'
            input.onchange = (e) => {
                const file = e.target.files?.[0]
                if (file) this.upload(file)
            }
            input.click()
        },
        async upload(file) {
            if (!file) return
            this.uploading = true
            try {
                const response = await Api.uploadImage(file)
                if (response?.error) {
                    emitToastr('error', response.message || 'Erro ao enviar imagem.')
                    return
                }
                this.emitImage(response.message)
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao enviar imagem.')
            } finally {
                this.uploading = false
            }
        },
        remove() {
            if (this.disabled) return
            this.emitImage('')
        },
        onDragOver() {
            if (this.disabled || this.uploading) return
            this.dragover = true
        },
        onDrop(event) {
            this.dragover = false
            if (this.disabled || this.uploading) return
            const file = event.dataTransfer?.files?.[0]
            if (!file) return
            if (!file.type.startsWith('image/')) {
                emitToastr('error', 'Apenas arquivos de imagem são aceitos.')
                return
            }
            this.upload(file)
        },
        togglePixibay() {
            this.pixibayOpen = !this.pixibayOpen
        },
        resetSearch() {
            this.query = ''
            this.items = []
            this.page = 1
            this.hasMore = false
        },
        async runSearch(query) {
            this.items = []
            this.page = 1
            this.hasMore = false
            if (!query || !query.trim()) return
            this.searching = true
            try {
                const response = await Api.getImages({ query: query.trim(), page: 1 })
                if (response?.error) {
                    emitToastr('error', response.message || 'Erro na busca.')
                    return
                }
                const hits = response?.message?.hits || []
                this.items = hits
                this.hasMore = hits.length > 0
            } catch (err) {
                emitToastr('error', err?.message || 'Erro na busca.')
            } finally {
                this.searching = false
            }
        },
        async loadMore() {
            if (!this.query.trim() || this.loadingMore) return
            this.loadingMore = true
            this.page += 1
            try {
                const response = await Api.getImages({ query: this.query.trim(), page: this.page })
                if (response?.error) {
                    emitToastr('error', response.message || 'Erro ao carregar mais.')
                    return
                }
                const hits = response?.message?.hits || []
                this.items.push(...hits)
                this.hasMore = hits.length > 0
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao carregar mais.')
            } finally {
                this.loadingMore = false
            }
        },
        select(item) {
            if (!item) return
            const url = item.largeImageURL || item.webformatURL
            if (!url) return
            this.emitImage(url)
        },
    },
    watch: {
        query: debounce(function (q) {
            this.runSearch(q)
        }, 450),
    },
}
</script>

<style scoped>
.artigos-image-picker {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.picker-frame {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    background: #f4f6f9;
    border: 2px dashed #d0d7de;
    transition: border-color 180ms ease, background 180ms ease, transform 180ms ease;
    cursor: pointer;
}

.picker-frame--has-image {
    border-style: solid;
    border-color: transparent;
    background: #000;
    cursor: default;
}

.picker-frame--dragover {
    border-color: var(--v-primary-base, #4a5cff);
    background: rgba(74, 92, 255, 0.06);
    transform: scale(1.005);
}

.picker-frame--disabled {
    opacity: 0.6;
    pointer-events: none;
}

.picker-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.picker-empty {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-align: center;
    padding: 16px;
}

.picker-empty-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(74, 92, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 6px;
}

.picker-empty-title {
    font-weight: 600;
    color: #1a2236;
    font-size: 15px;
}

.picker-empty-hint {
    color: #6a7383;
    font-size: 13px;
}

.picker-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(15, 20, 35, 0.6);
    color: #fff;
    opacity: 0;
    transition: opacity 180ms ease;
}

.picker-overlay--loading {
    opacity: 1;
    flex-direction: column;
    background: rgba(255, 255, 255, 0.92);
    color: #1a2236;
}

.picker-frame--has-image:hover .picker-overlay--actions {
    opacity: 1;
}

.picker-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.pixibay-panel {
    background: #f8fafc;
    border-radius: 12px;
    padding: 14px;
    border: 1px solid #e6eaf0;
}

.pixibay-search {
    margin-bottom: 12px;
}

.pixibay-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 8px;
}

.pixibay-tile {
    position: relative;
    aspect-ratio: 1 / 1;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid transparent;
    cursor: pointer;
    padding: 0;
    background: #e6eaf0;
    transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.pixibay-tile img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.pixibay-tile:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(15, 20, 35, 0.12);
    border-color: var(--v-primary-base, #4a5cff);
}

.pixibay-tile--selected {
    border-color: var(--v-primary-base, #4a5cff);
    box-shadow: 0 0 0 2px rgba(74, 92, 255, 0.2);
}

.pixibay-tile-check {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--v-primary-base, #4a5cff);
    display: none;
    align-items: center;
    justify-content: center;
}

.pixibay-tile--selected .pixibay-tile-check {
    display: flex;
}

.pixibay-skeleton-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 8px;
}

.pixibay-skeleton {
    aspect-ratio: 1 / 1;
    border-radius: 8px;
    background: linear-gradient(90deg, #e6eaf0 0%, #f1f4f8 50%, #e6eaf0 100%);
    background-size: 200% 100%;
    animation: pixibay-shimmer 1.4s infinite;
}

@keyframes pixibay-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

.pixibay-empty {
    text-align: center;
    color: #6a7383;
    font-size: 13px;
    padding: 20px 0;
}

.pixibay-load-more {
    text-align: center;
    margin-top: 10px;
}

.pixibay-fade-enter-active,
.pixibay-fade-leave-active {
    transition: opacity 180ms ease, transform 180ms ease;
}

.pixibay-fade-enter,
.pixibay-fade-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
</style>
